import {
  Menu,
  MenuItem,
  MenuSeparator,
} from '@/components/interactives/dropdown-menu.tsx'
import { columnsAtom, getColumnById } from '@/store/rundown.store.ts'
import {
  useColumnLayoutActions,
  useColumnLayoutColumns,
  useEffectiveHiddenColumns,
  useRawColumnLayout,
  useUrlHiddenColumns,
} from '@/shared/store/column-layout.store.ts'
import {
  faAdd,
  faEye,
  faPen,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'

import { isColumnsLimitExceededAtom } from '@/store/plan.store.ts'
import { RundownPermission } from '@rundown-studio/types'
import { useAtom, useAtomValue } from 'jotai'
import _indexOf from 'lodash/indexOf'
import { lazy, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getIsPrivateNotesColumn,
  useAddRundownColumn,
  useGetAllowedColumns,
  useReorderRundownColumns,
  useReorderRundownPrivateColumn,
  useUpdateRundownColumn,
} from '../../../entities/rundown/index.ts'
import { useUser } from '../../../entities/user/index.ts'
import { useRundownPermission } from '../../permissions/lib/use-rundown-permission.ts'
import { useModal } from '../../modal-manager/index.ts'
import { ColumnHeader } from './ColumnHeader.tsx'
import { GRID_DOM_HEADER } from '../grid/grid.const.ts'

const UpgradeModal = lazy(() => import('@/components/modal/UpgradeModal.tsx'))

export function ColumnHeaders() {
  const { rundownId } = useParams()
  const baseColumns = useAtomValue(columnsAtom)
  // Apply user's local layout (order + width overrides) on top of the base columns.
  const columns = useColumnLayoutColumns(rundownId, baseColumns)
  const columnsOrder = useMemo(() => columns.map((c) => c.id), [columns])

  const [columnById] = useAtom(getColumnById)
  const [draggedColumnId, setDraggedColumnId] = useState(null)
  const [draggedToPosition, setDraggedToPosition] = useState(null)
  const hiddenColumns = useEffectiveHiddenColumns(rundownId)
  const urlHiddenColumns = useUrlHiddenColumns()
  const layout = useRawColumnLayout(rundownId)
  const { patchLayout, resetLayout } = useColumnLayoutActions(rundownId)
  const isEditor = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const [upgradeModal, renderUpgradeModal] = useModal(UpgradeModal)

  const navigate = useNavigate()

  const { getAllowedColumns } = useGetAllowedColumns()
  const { data: user } = useUser()

  const allowedColumns = useMemo(
    () => getAllowedColumns(columns),
    [columns, getAllowedColumns],
  )

  const visibleColumns = useMemo(
    () => allowedColumns.filter((col) => !hiddenColumns.includes(col.id)),
    [allowedColumns, hiddenColumns],
  )

  const { mutate: addRundownColumn, isPending } = useAddRundownColumn({})
  const { mutate: updateRundownColumn } = useUpdateRundownColumn()
  const { mutate: reorderRundownColumns } = useReorderRundownColumns()
  const { mutate: reorderPrivateColumn } = useReorderRundownPrivateColumn()

  const handleShowColumn = useCallback(
    (columnId: string) => {
      const next = layout.hiddenColumns.filter((id) => id !== columnId)
      patchLayout({ hiddenColumns: next })
    },
    [layout.hiddenColumns, patchLayout],
  )

  const handleShowAll = useCallback(() => {
    patchLayout({ hiddenColumns: [] })
  }, [patchLayout])

  const hiddenByUser = useMemo(
    () =>
      layout.hiddenColumns.filter((id) => !urlHiddenColumns.includes(id)),
    [layout.hiddenColumns, urlHiddenColumns],
  )

  const isColumnLimitExceeded = useAtomValue(isColumnsLimitExceededAtom)

  const handleAddNewColumn = useCallback(() => {
    if (isColumnLimitExceeded) {
      upgradeModal.show()
      return // Abort action
    }

    addRundownColumn({ rundownId })
  }, [addRundownColumn, isColumnLimitExceeded, rundownId, upgradeModal])

  /**
   * Moves a column to an exact index in the full order array.
   * Always writes to local layout; additionally pushes to the DB for writers.
   */
  const handleReorderColumnsByIndex = useCallback(
    (columnId, moveTo) => {
      const oldIndex = _indexOf(columnsOrder, columnId)
      if (oldIndex === -1 || moveTo === -1) return

      const newIndex = moveTo > oldIndex ? moveTo - 1 : moveTo
      const next = [...columnsOrder]
      const [movedId] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, movedId)

      // Always write local
      patchLayout({ columnOrder: next })

      // Push to DB only for writers (or for the user's own private notes column)
      const movedColumn = columnById(columnId)
      const isPrivateNotesColumn = movedColumn
        ? getIsPrivateNotesColumn(movedColumn, user?.uid)
        : false

      if (isPrivateNotesColumn) {
        const afterColumnId = newIndex > 0 ? next[newIndex - 1] : null
        reorderPrivateColumn({
          rundownId,
          columnId,
          payload: { afterColumnId: afterColumnId ?? null },
        })
      } else if (isEditor) {
        reorderRundownColumns({ rundownId, columnIds: next })
      }
    },
    [
      columnById,
      columnsOrder,
      isEditor,
      patchLayout,
      reorderPrivateColumn,
      reorderRundownColumns,
      rundownId,
      user?.uid,
    ],
  )

  /**
   * Move a column left/right by one visible slot.
   */
  const handleReorderColumnsByDirection = useCallback(
    (columnId, direction) => {
      const visibleIndex = visibleColumns.findIndex((c) => c.id === columnId)
      if (visibleIndex === -1) return

      const target = direction === 'right' ? visibleIndex + 2 : visibleIndex - 1
      let orderIndex: number
      if (target >= visibleColumns.length) {
        const lastVisibleId = visibleColumns[visibleColumns.length - 1]?.id
        orderIndex = lastVisibleId
          ? _indexOf(columnsOrder, lastVisibleId) + 1
          : columnsOrder.length
      } else if (target < 0) {
        return
      } else {
        const targetId = visibleColumns[target]?.id
        orderIndex = targetId ? _indexOf(columnsOrder, targetId) : target
      }

      handleReorderColumnsByIndex(columnId, orderIndex)
    },
    [columnsOrder, handleReorderColumnsByIndex, visibleColumns],
  )

  const handleUpdateWidth = useCallback(
    (columnId, width) => {
      // Called by ColumnHeader only when the user has write access —
      // local is written inside ColumnHeader in all cases.
      updateRundownColumn({
        rundownId,
        columnId,
        payload: { width },
      })
    },
    [rundownId, updateRundownColumn],
  )

  const onDragStart = useCallback((columnId) => {
    setDraggedColumnId(columnId)
  }, [])

  const onDragEnd = useCallback(() => {
    setDraggedColumnId(null)
    setDraggedToPosition(null)
  }, [])

  const onDragOver = useCallback(
    (event, columnId) => {
      if (!draggedColumnId) return

      event.preventDefault() // Prevent default drag behavior

      const targetElement = event.currentTarget
      const elementWidth = targetElement.offsetWidth
      const mouseX = event.clientX
      const elementMidpoint =
        targetElement.getBoundingClientRect().left + elementWidth / 2

      const targetIndex = visibleColumns.findIndex((c) => c.id === columnId)
      const newIndex = mouseX > elementMidpoint ? targetIndex + 1 : targetIndex

      // Update the state to track the new drop index
      if (newIndex !== draggedToPosition) {
        setDraggedToPosition(newIndex)
      }
    },
    [visibleColumns, draggedColumnId, draggedToPosition],
  )

  function onDrop() {
    if (draggedColumnId !== null && draggedToPosition !== null) {
      // Convert visible index to columnsOrder index
      let orderIndex: number
      if (draggedToPosition >= visibleColumns.length) {
        const lastVisibleId = visibleColumns[visibleColumns.length - 1]?.id
        orderIndex = lastVisibleId
          ? _indexOf(columnsOrder, lastVisibleId) + 1
          : columnsOrder.length
      } else {
        const targetId = visibleColumns[draggedToPosition]?.id
        orderIndex = targetId
          ? _indexOf(columnsOrder, targetId)
          : draggedToPosition
      }
      handleReorderColumnsByIndex(draggedColumnId, orderIndex)
    }
    setDraggedColumnId(null)
    setDraggedToPosition(null)
  }

  return (
    <div role="rowgroup" id={GRID_DOM_HEADER} className="relative flex gap-1">
      <div
        role="row"
        aria-label="Rundown grid columns"
        className="relative flex gap-1"
        onDrop={onDrop}
      >
        {visibleColumns.map((column, index) => {
          return (
            <div
              key={column.id}
              role="columnheader"
              aria-labelledby={column.id}
              className="relative"
            >
              {/* On reorder, render the vertical bar if it's before this column */}
              {draggedColumnId !== null && draggedToPosition === index && (
                <div className="absolute h-full w-1 bg-blue-500/80 -left-1 rounded-full" />
              )}

              <ColumnHeader
                key={`${column?.id}_${_indexOf(columnsOrder, column?.id)}`}
                column={column}
                moveColumn={handleReorderColumnsByDirection}
                updateWidth={handleUpdateWidth}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                isDragging={draggedColumnId !== null}
              />
            </div>
          )
        })}

        {/* On reorder, render the vertical bar at the end if dragging beyond the last column */}
        {draggedColumnId !== null &&
          draggedToPosition === visibleColumns.length && (
            <div className="absolute h-full w-1 bg-blue-500/80 right-0 rounded-full" />
          )}
      </div>

      <div className="relative px-2">
        <Menu icon={faPen} label="Edit Columns" className="h-7!">
          {isEditor && (
            <MenuItem
              label={isColumnLimitExceeded ? 'Add column (limit reached)' : 'Add column'}
              onClick={handleAddNewColumn}
              icon={faAdd}
              disabled={isPending}
            />
          )}
          {!user?.uid && (
            <MenuItem
              label="Add private notes"
              icon={faAdd}
              onClick={() =>
                navigate(
                  `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                )
              }
            />
          )}
          <MenuSeparator />
          <Menu label="Hidden columns" icon={faEye}>
            {hiddenByUser.length === 0 && (
              <MenuItem disabled label="No hidden columns" />
            )}
            {hiddenByUser.map((c) => (
              <MenuItem
                key={c}
                label={columnById(c)?.name || 'Untitled'}
                onClick={() => handleShowColumn(c)}
              />
            ))}
            <MenuSeparator />
            <MenuItem
              label="Show all"
              onClick={handleShowAll}
              disabled={hiddenByUser.length === 0}
            />
          </Menu>
          <MenuSeparator />
          <MenuItem
            label="Reset column layout"
            onClick={resetLayout}
            icon={faRotateLeft}
          />
        </Menu>
      </div>

      {renderUpgradeModal({
        message: "You've reached your limit for columns on this rundown.",
      })}
    </div>
  )
}
